import React, { useMemo, useState } from 'react';
import { ref, set } from 'firebase/database';
import { ref as storageRef, uploadString } from 'firebase/storage';
import { auth, database, storage } from '../firebase';

type ToastType = 'neutral' | 'success' | 'error';

type QuestionnaireState = Record<string, string>;

const questionnaireFields = [
  'F1Q1',
  'F1Q2',
  'F1Q3',
  'F1Q4',
  'F2Q5',
  'F2Q6',
  'F3Q7_dominant',
  'F3Q7_matiz',
  'F3Q9',
  'F4Q10',
  'F4Q11',
  'F4Q12',
  'F4Q13',
  'F4Q14',
];

const createUuid4Hex = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '');
  }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  return Array.from(bytes)
    .map((item) => item.toString(16).padStart(2, '0'))
    .join('');
};

function CreateProjectSection() {
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [questionnaire, setQuestionnaire] = useState<QuestionnaireState>(
    Object.fromEntries(questionnaireFields.map((field) => [field, '']))
  );
  const [status, setStatus] = useState('');
  const [statusType, setStatusType] = useState<ToastType>('neutral');
  const [isCreating, setIsCreating] = useState(false);
  const [createdProjectId, setCreatedProjectId] = useState('');

  const isFormValid = useMemo(
    () =>
      nombre.trim().length > 0 &&
      email.trim().length > 0 &&
      questionnaireFields.every((field) => (questionnaire[field] ?? '').trim().length > 0),
    [nombre, email, questionnaire]
  );

  const handleGenerateProject = async () => {
    const user = auth.currentUser;
    if (!user) {
      setStatus('Inicia sesión para crear proyecto.');
      setStatusType('error');
      return;
    }

    if (!isFormValid) {
      setStatus('Completa Nombre, Email y todas las preguntas del cuestionario.');
      setStatusType('error');
      return;
    }

    setIsCreating(true);
    setStatus('Creando proyecto...');
    setStatusType('neutral');

    const projectId = createUuid4Hex();
    const payload = {
      ...questionnaire,
      Nombre: nombre.trim(),
      Email: email.trim(),
      project_id: projectId,
    };

    try {
      await set(ref(database, `Projects/${projectId}`), {
        Nombre: nombre.trim(),
        Email: email.trim(),
        created: Math.floor(Date.now() / 1000),
      });

      await uploadString(
        storageRef(storage, `${projectId}/JSONS/cuestionario.json`),
        JSON.stringify(payload, null, 2),
        'raw',
        { contentType: 'application/json' }
      );

      setCreatedProjectId(projectId);
      setStatus(`Proyecto creado: ${projectId}`);
      setStatusType('success');
    } catch (error) {
      setStatus('Error al crear el proyecto.');
      setStatusType('error');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="section">
      <div className="section-header">
        <h2>Crear nuevo proyecto</h2>
      </div>

      {createdProjectId ? <div className="hint">Project ID creado: {createdProjectId}</div> : null}

      <div className="prompt-ready">
        <label className="field">
          <span>Nombre</span>
          <input
            className="table-input"
            type="text"
            value={nombre}
            onChange={(event) => setNombre(event.target.value)}
          />
        </label>

        <label className="field">
          <span>Email</span>
          <input
            className="table-input"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>

        <div className="project-table">
          <div className="project-table-header">
            <h3>Cuestionario</h3>
          </div>
          <div className="table table-compact">
            <div className="table-row table-header table-two-columns">
              <div>Campo</div>
              <div>Valor</div>
            </div>
            {questionnaireFields.map((field) => (
              <div className="table-row table-two-columns" key={field}>
                <div className="mono">{field}</div>
                <div>
                  <textarea
                    className="table-input prompt-tester-textarea"
                    value={questionnaire[field] ?? ''}
                    onChange={(event) =>
                      setQuestionnaire((prev) => ({
                        ...prev,
                        [field]: event.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="panel-actions-inline">
          <button
            className="primary-button"
            type="button"
            onClick={handleGenerateProject}
            disabled={isCreating}
          >
            {isCreating ? 'Generando...' : 'Generar proyecto'}
          </button>
        </div>
        {status ? <div className={`inline-toast inline-toast-${statusType}`}>{status}</div> : null}
      </div>
    </div>
  );
}

export default CreateProjectSection;
